import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Stack, ToggleButtonGroup, Typography } from '@monorepo/nova-ui';
import { AssetClass } from '@monorepo/types';
import { DarkHeaderIllustration, LightHeaderIllustration } from './components';
import { useOnboardingAssetsModalState } from './hooks/use-onboarding-assets-modal-state';
import {
  StyledButton,
  StyledButtonContainer,
  StyledDialog,
  StyledDialogContent,
  StyledIllustration,
  StyledSubtitle,
  StyledTitle,
  StyledToggleButton,
} from './styled';

export interface OnboardingAssetsModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: (selectedAsset: AssetClass[]) => void;
}

export const OnboardingAssetsModal = ({
  open,
  onClose,
  onContinue,
}: OnboardingAssetsModalProps) => {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const { selectedAsset, isSaving, onChange, onContinueClick, onDialogClose } =
    useOnboardingAssetsModalState({ open, onClose, onContinue });

  return (
    <StyledDialog
      open={open}
      onClose={onDialogClose}
      disableEscapeKeyDown
      variant="bare"
      maxWidth="sm"
      fullWidth
    >
      <StyledDialogContent>
        <StyledIllustration>
          {theme.palette.mode === 'dark' ? (
            <DarkHeaderIllustration />
          ) : (
            <LightHeaderIllustration />
          )}
        </StyledIllustration>

        <StyledTitle variant="h3" component="h1">
          {t('assets_modal.title')}
        </StyledTitle>

        <StyledSubtitle variant="subtitle1">
          {t('assets_modal.subtitle')}
        </StyledSubtitle>

        <ToggleButtonGroup
          value={selectedAsset}
          exclusive
          onChange={onChange}
          aria-label={t('assets_modal.aria_label.asset_selection')}
        >
          <StyledToggleButton
            value="forex"
            aria-label={t('assets_modal.aria_label.forex')}
          >
            <Stack direction="row" alignItems="center" gap={1.5}>
              <SystemIcon
                iconName="CurrencyCircleDollar"
                size={16}
                color={
                  selectedAsset?.includes(AssetClass.Forex)
                    ? theme.palette.mode === 'dark'
                      ? theme.palette.text.black
                      : theme.palette.text.opposite
                    : theme.palette.text.secondary
                }
              />
              <Typography variant="body1">
                {t('assets_modal.options.forex')}
              </Typography>
            </Stack>
          </StyledToggleButton>

          <StyledToggleButton
            value="futures"
            aria-label={t('assets_modal.aria_label.futures')}
          >
            <Stack direction="row" alignItems="center" gap={1.5}>
              <SystemIcon
                iconName="Invoice"
                size={16}
                color={
                  selectedAsset?.includes(AssetClass.Futures)
                    ? theme.palette.mode === 'dark'
                      ? theme.palette.text.black
                      : theme.palette.text.opposite
                    : theme.palette.text.secondary
                }
              />
              <Typography variant="body1">
                {t('assets_modal.options.futures')}
              </Typography>
            </Stack>
          </StyledToggleButton>

          <StyledToggleButton
            value="both"
            aria-label={t('assets_modal.aria_label.both')}
          >
            <Typography variant="body1">
              {t('assets_modal.options.both')}
            </Typography>
          </StyledToggleButton>
        </ToggleButtonGroup>

        <StyledButtonContainer>
          <StyledButton
            variant="outlined"
            onClick={onContinueClick}
            disabled={!selectedAsset || isSaving}
          >
            {isSaving
              ? t('assets_modal.saving', { defaultValue: 'Saving...' })
              : t('assets_modal.button')}
          </StyledButton>
        </StyledButtonContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
};
